/* eslint-disable padding-line-between-statements */
/* eslint-disable no-console */
import { Grid } from '@mui/material';
import AppStoreLinksBanner from 'components/AppStoreLinksBanner/AppStoreLinksBanner';
import Header from 'components/Header/Header';
import HeaderIconLogo from 'components/Header/header-items/HeaderIconLogo';
import HeaderItem from 'components/Header/header-items/HeaderItem';
import HomeButton from 'components/HomeButton/HomeButton';
import Loader from 'components/Loader/Loader';
import NotFound from 'components/NotFound/NotFound';
import OptionedMenu from 'components/OptionedMenu/OptionedMenu';
import { ProductDetails, ProductImagesNoCrop, RelatedProducts } from 'components/page-product';
import config, { Language } from 'config';
import { BUY_PRODUCT_CANCEL, BUY_PRODUCT_SUCCESS, INDEX, PRODUCT, PRODUCT_EDIT } from 'config/fe-routes';
import { useRouter } from 'next/router';
import { BreadcrumbJsonLd, NextSeo, ProductJsonLd } from 'next-seo';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import ConditionService from 'services/condition.service';
import ProductService from 'services/product.service';
import { RecsysTracking } from 'services/recsysTracking.service';
import { likeServiceActions } from 'store/actions/like-service.actions';
import { getMemoize, setMemoize } from 'store/actions/memoize.actions';
import { markProductDeleted, moveProductToAvailable, moveProductToOtherStatuses } from 'store/actions/my-shop-page.actions';
import { useAppDispatch } from 'store/hooks';
import { searchSessionActions } from 'store/reducers/searchSession.reducer';
import { HOME_FEED_ENGINES, HomeFeedEngine, ProductCondition, ProductStatusType, ProductType } from 'types/product.types';
import { UserType } from 'types/user.types';
import { GTM, GTMLocations } from 'utils/analytics';
import { buildCanonicalPath, getBaseUrl } from 'utils/canonical-url';
import { getSafeText } from 'utils/format-text';
import { redirectTo } from 'utils/route-helpers';
import { getPreviousHistoryTrackerRoute } from 'utils/route-history-tracker';
import { useIsScreenSizeMax } from 'utils/screen-size';
import { getJsonLdBreadcrumbsList, getProductSEOFriendlyTitle } from 'utils/seo-data';
import { formatServerSideProps } from 'utils/server-side-props';
import { useNotification } from 'utils/snackbar';

import { LikeButton } from '../../../../components/buttons/LikeButton';
import { useAttributionOrigin } from '../../../../hooks/useAttributionOrigin';
import { useLanguage } from '../../../../hooks/useLanguage';

const productService = new ProductService();
const conditionService = new ConditionService();

type ProductProps = {
  productSlug: string;
  user: UserType;
  initialProduct: ProductType;
  availableConditions: ProductCondition[];
  historyData: any;
  flags: any;
  jsonLdBreadcrumbs: any[];
};

const productSEO = {
  inStock: 'https://schema.org/InStock',
  outOfStock: 'https://schema.org/OutOfStock',
  conditions: {
    Keskmine: 'https://schema.org/DamagedCondition',
    Hea: 'https://schema.org/UsedCondition',
    Uueväärne: 'https://schema.org/UsedCondition',
    Uus: 'https://schema.org/NewCondition',
    New: 'https://schema.org/NewCondition',
    'Like New': 'https://schema.org/UsedCondition',
    Good: 'https://schema.org/UsedCondition',
    Fair: 'https://schema.org/DamagedCondition',
    used: 'https://schema.org/UsedCondition',
  },
};

const productStatuses = {
  published: 'published',
  hidden: 'hidden',
};

const mapStateToProps = ({ userData, historyData }) => ({
  user: userData && userData.user,
  historyData,
});

const PRODUCT_CANONICAL_ROUTE = '/[shop-slug]/product/[product-slug]';

// eslint-disable-next-line
const ProductPage = (props: ProductProps) => {
  const { productSlug, user, initialProduct, availableConditions, historyData, jsonLdBreadcrumbs } = props;
  const { likedProducts } = user;

  const router = useRouter();

  const { t } = useTranslation();
  const { language } = useLanguage();
  const { showErrorNotification } = useNotification();

  const [product, setProduct] = useState(initialProduct);
  const [likeCount, setLikeCount] = useState(() => product?.likeCount || 0);
  const [updateGetOptions, setUpdateGetOptions] = useState(false);
  const [breadcrumbsList, setBreadcrumbsList] = useState(jsonLdBreadcrumbs);
  const [isLoading, setLoading] = useState(false);
  const [productSEOTitle, setProductSEOTitle] = useState<string>('');

  const isMobileScreenSize = useIsScreenSizeMax('md');
  const isProductDeleted = product?.status === 'deleted';
  const isProductEraser = +product?.deleted_by === +user?.id;
  const shopSlug = router.query['shop-slug'];

  // for analytics
  const rank = router.query['rank'];
  const sourceModule = router.query['source_module']?.toString();
  const incomingSearchGuid = router.query['searchGuid']?.toString();
  // Set when the PDP was opened from a recommendation card; mutually exclusive with searchGuid.
  const incomingRecommendationId = router.query['recommendationId']?.toString();
  // Set when the PDP was opened from the home feed, on every arm — only the advec arm also has an
  // id. Comes from the clicked card itself, so it stays correct when a later feed page was served
  // by a fallback engine. Whitelisted: the API validates it as a strict enum and would 400 the
  // like/offer/order request an unknown value rides on.
  const rawRecommendationEngine = router.query['recommendationEngine']?.toString();
  const incomingRecommendationEngine = HOME_FEED_ENGINES.includes(rawRecommendationEngine as HomeFeedEngine)
    ? (rawRecommendationEngine as HomeFeedEngine)
    : undefined;
  const dispatch = useAppDispatch();
  const attributionOrigin = useAttributionOrigin(productSlug);

  // Captured at render, per slug: the URL-cleaning effect below strips these params on the same
  // mount, and auth can resolve after that strip — reading router.query inside the view effect
  // would then find nothing. Re-keyed on slug so PDP→PDP nav cannot inherit a stale origin.
  const incomingOriginRef = useRef<{
    slug?: string;
    searchGuid?: string;
    recommendationId?: string;
    recommendationEngine?: HomeFeedEngine;
  }>({});
  if (incomingOriginRef.current.slug !== productSlug) {
    incomingOriginRef.current = {
      slug: productSlug,
      searchGuid: incomingSearchGuid,
      recommendationId: incomingRecommendationId,
      recommendationEngine: incomingRecommendationEngine,
    };
  }

  const getProductData = async (ignoreLoader = false) => {
    setLoading(!ignoreLoader);

    const slug: string = Array.isArray(productSlug) ? productSlug[0] : productSlug;

    // Get product
    try {
      const newProduct = await productService.getProductBySlug(slug, {}, { silentFail: true });

      setProduct(newProduct);
    } catch (e) {
      // Do nothing
    }

    setLoading(false);
  };

  // Mark sold
  const markSold = async () => {
    setLoading(true);

    try {
      await productService.updateProductBySlug(product.slug, {
        status: 'sold',
      });
      moveProductToOtherStatuses({ ...product, status: 'sold' as ProductStatusType });
      await getProductData();
    } catch (e) {
      // Do nothing
    }

    setLoading(false);
  };

  // Change product visibility
  const changeVisibility = async () => {
    setLoading(true);
    const toStatus: ProductStatusType = product.status !== 'not-visible' ? 'not-visible' : 'published';

    try {
      await productService.updateProductBySlug(product.slug, { status: toStatus }, { silentFail: true });
      if (toStatus === 'not-visible') {
        moveProductToOtherStatuses({ ...product, status: toStatus });
      } else {
        moveProductToAvailable({ ...product, status: toStatus });
      }
      await getProductData();
    } catch (e) {
      showErrorNotification({ error: e });
    }

    setLoading(false);
  };

  // Delete product
  const deleteProduct = async () => {
    setLoading(true);

    try {
      await productService.removeProduct(product.slug);
      markProductDeleted(String(product.id));
      await getProductData();
    } catch (e) {
      // Do nothing
    }

    setLoading(false);
  };

  // Edit product
  const onEditProduct = () => {
    redirectTo(PRODUCT_EDIT(shopSlug as string, productSlug));
  };

  const toggleLike = async () => {
    const likedData = {
      liked: likedProducts ? !likedProducts[product.id] : true,
      id: product.id,
      ...attributionOrigin,
    };

    if (likedProducts) {
      setLikeCount((prev) => (likedData.liked ? prev + 1 : prev > 0 ? prev - 1 : 0));
    }

    likeServiceActions.toggleLikeState(likedData);
  };

  const handleModalLikeToggle = (liked: boolean) => {
    setLikeCount((prev) => (liked ? prev + 1 : prev > 0 ? prev - 1 : 0));
    likeServiceActions.toggleLikeLocalState({ id: product.id, liked });
  };

  // PRODUCT() resolves to /toode/ for Estonian users, so it cannot be used here: the
  // structured data and og:url must always name the one official /product/ address.
  const canonicalPath = buildCanonicalPath(router.route, router.query, router.asPath);
  const canonicalUrl = `${getBaseUrl()}${canonicalPath}`;

  const initSeoBreadcrumbs = () => {
    const breadcrumbs = getJsonLdBreadcrumbsList(
      { asPath: canonicalPath },
      {
        productSlug: product?.slug,
        name: getSafeText(product?.description, 40),
      },
    );

    setBreadcrumbsList(breadcrumbs);
  };

  const getProductConditionSchema = () => {
    const schemaLink = productSEO.conditions.used;

    if (product?.condition?.name) {
      return productSEO.conditions[product?.condition?.name] || schemaLink;
    }

    return schemaLink;
  };

  const getOptions = () => {
    const options = [{ name: t('components.report.title'), actionType: 'report' }];

    if (user.is_admin) {
      if (product.pinnedAt) {
        options.push({
          name: t('components.admin.product.remove-pin'),
          actionType: 'unpin',
        });
      } else {
        options.push({
          name: t('components.admin.product.pin'),
          actionType: 'pin',
        });
      }

      options.push(
        { name: t('components.admin.product.hide'), actionType: 'hide' },
        { name: t('components.admin.product.delete'), actionType: 'delete' },
      );
    }

    return options;
  };

  // When product page changes, reset product to new initialShop
  useEffect(() => {
    // Fetch new product when it changes
    if (product?.slug !== productSlug) {
      setProduct(initialProduct);

      // Track product views
      GTM.viewItem(initialProduct, getViewItemAttribution(rank, sourceModule));
    }
  }, [shopSlug, initialProduct, rank, sourceModule]);

  // Fire recsys product-view once per product slug (guards against StrictMode double-fire).
  const lastTrackedProductSlugRef = useRef<string | undefined>();
  useEffect(() => {
    if (!user?.id || !product?.id || !product?.slug) {
      return;
    }
    if (lastTrackedProductSlugRef.current === product.slug) {
      return;
    }
    lastTrackedProductSlugRef.current = product.slug;
    const { searchGuid, recommendationId, recommendationEngine } = incomingOriginRef.current;
    RecsysTracking.productView({
      product: { id: product.id, slug: product.slug },
      searchGuid,
      recommendationId: searchGuid ? undefined : recommendationId,
      recommendationEngine: searchGuid ? undefined : recommendationEngine,
      isOwnProduct: !!product.isOwner,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, product?.id, product?.slug]);

  useEffect(() => {
    // First-mount GTM (subsequent PDP-nav GTM is handled by the effect above).
    GTM.viewItem(product, getViewItemAttribution(rank, sourceModule));
  }, []);

  // Capture the click origin into Redux + clean URL on every PDP load (including PDP→PDP nav,
  // where the component instance is reused and a [] effect would not re-run).
  useEffect(() => {
    if (incomingSearchGuid || incomingRecommendationId || incomingRecommendationEngine) {
      dispatch(
        searchSessionActions.setActiveOrigin({
          productSlug,
          searchGuid: incomingSearchGuid,
          recommendationId: incomingRecommendationId,
          recommendationEngine: incomingRecommendationEngine,
        }),
      );
    } else {
      dispatch(searchSessionActions.clearActiveOrigin());
    }

    // Remove rank/searchGuid/recommendationId/recommendationEngine params from url; preserve source_module so checkout can pick it up at click time.
    // Shallow replace: getServerSideProps already ran with the params, so skip a redundant refetch.
    if (initialProduct?.shop?.active_slug && initialProduct?.slug) {
      redirectTo(
        {
          ...PRODUCT(initialProduct.shop.active_slug, initialProduct.slug),
          ...(sourceModule && { query: { source_module: sourceModule } }),
          opts: { shallow: true },
        },
        true,
      );
    }
  }, [productSlug]);

  useEffect(() => {
    if (product) {
      setProductSEOTitle(getProductSEOFriendlyTitle(product));

      initSeoBreadcrumbs();
      setLikeCount(product.likeCount || 0);
    }
  }, [product]);

  useEffect(() => {
    setUpdateGetOptions(false);
    getOptions();
  }, [updateGetOptions]);

  const renderBackOrBurger = () => {
    const editSlug = language === Language.et ? 'muuda' : 'edit';
    const lastPageIsEdit = historyData?.history[historyData?.history.length - 2]?.as?.endsWith(editSlug);

    if (!lastPageIsEdit && historyData?.history?.length > 1) {
      return (
        <HeaderIconLogo
          icon="back"
          showLogo={false}
          iconOnClick={(history) => {
            const previousHref = getPreviousHistoryTrackerRoute(history)?.href;
            const comingFromCheckoutPage = [
              BUY_PRODUCT_SUCCESS('', '').href,
              BUY_PRODUCT_CANCEL('', '').href,
              '/checkout/success',
              '/checkout/cancel',
            ].some((routeHref) => previousHref?.startsWith(routeHref));

            // If previous page was order success or fail page, override back button to go to index page
            if (comingFromCheckoutPage) {
              redirectTo(INDEX());

              return false;
            }

            return true;
          }}
        />
      );
    }

    return <HeaderIconLogo icon="home" showLogo={false} />;
  };

  const [isComponentMounted, setIsComponentMounted] = useState(false);

  useEffect(() => setIsComponentMounted(true), []);

  const header = (
    <Header className="product-page-header" disableOffsetAdjustment={isMobileScreenSize}>
      {[
        [
          renderBackOrBurger(),

          !isProductDeleted && product && (
            <HeaderItem>
              {user && user?.id && (
                <OptionedMenu
                  user={user}
                  product={product}
                  targetUser={product.user}
                  targetShop={product.shop}
                  refreshParentDataFn={getProductData}
                />
              )}
              {!product.shop.isBlocked && !product.isOwner && (
                <LikeButton liked={likedProducts && likedProducts[product?.id]} onClick={toggleLike} large />
              )}
            </HeaderItem>
          ),
        ],
      ]}
    </Header>
  );

  // No product found
  if (!product || (initialProduct?.status === 'not-visible' && !initialProduct?.isOwner)) {
    return (
      <>
        {header}

        <NotFound target="product" />
      </>
    );
  }

  return (
    <>
      <NextSeo
        title={productSEOTitle}
        openGraph={{
          title: productSEOTitle,
          url: canonicalUrl,
          description: product?.description,
          images: [
            {
              url: product?.images?.[0]?.gallery,
              width: 600,
              height: 600,
              alt: getShopNameWithDescription(product),
            },
          ],
          type: 'product',
        }}
      />

      <ProductJsonLd
        productName={getSafeText(product?.description, 100, 'Product name missing')}
        description={getSafeText(product?.description, 255, 'Product description missing')}
        images={product?.images?.map((img) => img.gallery)}
        brand={product?.brand ? product?.brand : 'NoBrand'}
        sku={product?.id.toString()}
        offers={[
          {
            price: product?.price,
            priceCurrency: product?.currency,
            availability: product?.status === productStatuses.published ? productSEO.inStock : productSEO.outOfStock,
            itemCondition: getProductConditionSchema(),
            url: canonicalUrl,
            seller: {
              name: product?.shop?.activeSlug,
            },
          },
        ]}
      />

      {breadcrumbsList?.length > 0 && <BreadcrumbJsonLd itemListElements={breadcrumbsList} />}

      {/* HEADER */}
      {header}

      {/* CONTENT */}
      <Grid container className="product-page page-wide">
        {(!isProductDeleted && product) || (isProductDeleted && isProductEraser) ? (
          isLoading ? (
            <Loader mt={isMobileScreenSize ? 12 : 4} />
          ) : (
            <>
              {/* IMAGES */}
              <ProductImagesNoCrop product={product} />

              {/* DETAILS */}
              <ProductDetails
                product={product}
                availableConditions={availableConditions}
                likeCallback={toggleLike}
                likeCount={likeCount}
                onMarkSold={() => markSold()}
                onVisibilityChange={() => changeVisibility()}
                onDelete={() => deleteProduct()}
                onEdit={() => onEditProduct()}
                onLikeToggle={handleModalLikeToggle}
                refreshParentDataFn={getProductData}
              />

              {user?.isLoggedIn && !user?.isAppUser ? (
                <AppStoreLinksBanner
                  linksLocation="listing"
                  GTMLocation={GTMLocations.LISTING_PAGE}
                  className="product-page-appstore-banner"
                />
              ) : null}

              {/* depict.ai recommendations container */}
              {!user?.is_admin && !config.productRecommendations.enabled && isComponentMounted && <div className="depict_product_page" />}

              {(user?.is_admin || config.productRecommendations.enabled) && <RelatedProducts product={product} />}

              <HomeButton className="mt-4 mx-2" />
            </>
          )
        ) : (
          <div className="deleted-product-view">
            {/* DELETED (NON-OWNER VIEW) */}
            <div className="deleted-product-view-emoji">😥</div>
            <h2 className="deleted-product-view-message">{t('components.product-edit.deleted')}</h2>
            <div className="deleted-product-view-discover">{t('components.product-edit.discover-more')}</div>

            {!isLoading && <HomeButton className="deleted-product-home-btn mt-4" />}
          </div>
        )}
      </Grid>
    </>
  );
};

function shortenDescription(desc) {
  if (desc.length < 60) {
    return desc;
  }
  if (desc.includes('.')) {
    return shortenDescription(desc.split('.')[0]);
  }
  if (desc.includes(',')) {
    return shortenDescription(desc.split(',')[0]);
  }

  const arr = desc.split(' ');
  let s = '';
  let str = '';

  while (s.length < 60) {
    str = s;
    s += ' ' + arr.shift();
  }

  return str;
}

function getShopNameWithDescription(product) {
  if (!product?.description) {
    return '';
  }

  const desc = shortenDescription(product?.description);

  return `@${product?.shop?.activeSlug} - ${desc}`;
}

export default connect(mapStateToProps)(ProductPage);

export const getServerSideProps = async (ctx) => {
  const { query } = ctx;
  const productSlug = query['product-slug'];

  const asPath = ctx?.req?.url;

  const getAvailableConditions = async () => {
    let availableConditions: ProductCondition[] = [];

    try {
      // Try to get product conditions from the memoize redux
      availableConditions = getMemoize('productConditions');

      // If data not available in store, fetch from API and store it, by default memoize timeout is 1 hour
      if (!availableConditions?.length) {
        availableConditions = await conditionService.getConditions();
        setMemoize({ productConditions: availableConditions });
      }
    } catch (e) {
      console.error(e);
    }

    return availableConditions;
  };

  const getProduct = async () => {
    let product = null;
    let jsonLdBreadcrumbs = [];

    try {
      product = await productService.getProductBySlug(productSlug, {}, { ctx });
      if (asPath) {
        // Crawlers read the SSR breadcrumbs, so these must name the canonical /product/
        // spelling rather than whichever alias the request came in on.
        jsonLdBreadcrumbs = getJsonLdBreadcrumbsList(
          { asPath: buildCanonicalPath(PRODUCT_CANONICAL_ROUTE, query, asPath) },
          {
            productSlug: product?.slug,
            name: getSafeText(product?.description, 40, 'Product name missing'),
          },
        );
      }
    } catch (e) {
      // Only a definitive API 404 may become a page 404 — transient failures
      // (5xx, timeouts) must render a 500 so crawlers retry instead of deindexing.
      if (e?.data?.status !== 404) {
        throw e;
      }
    }

    return { initialProduct: product, jsonLdBreadcrumbs };
  };

  const { initialProduct, jsonLdBreadcrumbs } = await getProduct();

  if (!initialProduct) {
    return { notFound: true };
  }

  const availableConditions = await getAvailableConditions();

  return {
    props: formatServerSideProps({
      productSlug,
      initialProduct,
      availableConditions,
      jsonLdBreadcrumbs,
    }),
  };
};

function getViewItemAttribution(rank, sourceModule) {
  return {
    ...(rank && { rank_in_search: Number(rank) }),
    ...(sourceModule && { source_module: sourceModule }),
  };
}
